import React from "react";
import { Bike, Clock, MessageCircle, ShoppingBag, Store } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  buildWhatsAppUrl,
  getWhatsAppNumber,
  isCompanyOpen,
} from "../../lib/company";
import { getCachedBranding } from "../../lib/branding";
import { CompanyLogo } from "../ui/CompanyLogo";

const DEFAULT_ANNOUNCEMENT =
  "Feito artesanalmente • Delivery e retirada • Peça online!";

/**
 * Header da loja (layout docs/tela): barra de aviso + identidade da loja + ações.
 * Não é fixo: a barra de busca/categorias (SearchAndFilters) é que fica "sticky".
 *
 * @param {Object|null} company - Dados de GET /companies/{code} (useCompany)
 * @param {number} totalItems - Número total de itens no carrinho
 * @param {number} totalPrice - Valor total do carrinho
 * @param {function} formatBRL - Formatador de moeda
 * @param {function} onCartClick - Abre o carrinho
 */
export const Header = ({
  company = null,
  totalItems = 0,
  totalPrice = 0,
  formatBRL = (value) => value,
  onCartClick = () => {},
}) => {
  const companyName = company?.name || getCachedBranding()?.name || "";
  const description =
    company?.description ||
    "Delícias artesanais feitas com amor e ingredientes selecionados.";
  const open = isCompanyOpen(company);
  const hasDelivery = company?.delivery !== false;
  const announcement =
    company?.deliveryDescription ||
    company?.pickupDescription ||
    DEFAULT_ANNOUNCEMENT;
  const whatsappUrl = buildWhatsAppUrl(
    getWhatsAppNumber(company),
    `Olá! Gostaria de fazer um pedido no ${companyName}.`
  );

  return (
    <>
      {/* Barra de aviso */}
      <aside className="bg-brand-700 text-white text-xs font-medium py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 min-w-0">
          {open !== null && (
            <span
              className={cn(
                "flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wide",
                open ? "bg-brand-accent text-brand-on-accent" : "bg-white/20 text-white"
              )}
            >
              {open ? "Aberto agora" : "Fechado agora"}
            </span>
          )}
          <span className="truncate">{announcement}</span>
        </div>
      </aside>

      {/* Identidade da loja */}
      <header className="bg-white border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-6">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-2xl bg-white ring-4 ring-brand-50 shadow-card overflow-hidden flex items-center justify-center p-1.5">
                <CompanyLogo
                  company={company}
                  alt={companyName}
                  className="w-full h-full object-contain"
                />
              </div>

              <div className="min-w-0">
                <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-tight text-cocoa leading-tight truncate">
                  {companyName}
                </h1>
                <p className="text-xs sm:text-sm text-cocoa-muted font-medium mt-0.5 line-clamp-2">
                  {description}
                </p>

                {/* Metadados rápidos */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-cocoa-muted mt-2 font-medium">
                  {open !== null && (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md font-semibold",
                        open
                          ? "bg-success-light text-success"
                          : "bg-red-50 text-red-700"
                      )}
                    >
                      <span
                        className={cn(
                          "w-2 h-2 rounded-full",
                          open ? "bg-success animate-pulse" : "bg-red-500"
                        )}
                      />
                      {open ? "Aberto" : "Fechado no momento"}
                    </span>
                  )}
                  {company?.businessHoursWeekdays && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-brand-600" />
                      {company.businessHoursWeekdays}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    {hasDelivery ? (
                      <Bike className="w-3.5 h-3.5 text-brand-600" />
                    ) : (
                      <Store className="w-3.5 h-3.5 text-brand-600" />
                    )}
                    {hasDelivery ? "Delivery e retirada" : "Somente retirada"}
                  </span>
                </div>
              </div>
            </div>

            {/* Ações: WhatsApp e sacola */}
            <div className="flex items-center gap-2 sm:gap-3 justify-end w-full md:w-auto flex-shrink-0">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Falar no WhatsApp"
                className="flex-1 min-w-[44px] sm:flex-none inline-flex items-center justify-center gap-2 min-h-[44px] px-3.5 text-xs font-bold text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-colors"
              >
                <MessageCircle className="w-4 h-4 text-emerald-600" />
                {/* Em telas muito estreitas (< 360px) fica só o ícone */}
                <span className="hidden min-[360px]:inline">WhatsApp</span>
              </a>

              <button
                type="button"
                onClick={onCartClick}
                aria-label={`Abrir sacola (${totalItems} ${
                  totalItems === 1 ? "item" : "itens"
                })`}
                className="relative flex-[2] sm:flex-none inline-flex items-center justify-center gap-2.5 min-h-[44px] bg-brand-600 hover:bg-brand-700 active:scale-95 text-white px-4 sm:px-5 rounded-xl shadow-float transition font-bold text-xs uppercase tracking-wider"
              >
                <span className="relative">
                  <ShoppingBag className="w-5 h-5" />
                  {totalItems > 0 && (
                    <span className="absolute -top-2 -right-2.5 bg-brand-accent text-brand-on-accent text-[10px] font-extrabold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow">
                      {totalItems}
                    </span>
                  )}
                </span>
                <span>Sacola</span>
                {totalItems > 0 && (
                  <span className="font-extrabold text-brand-accent normal-case tracking-normal">
                    {formatBRL(totalPrice)}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
